import { MutableRefObject, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { produce } from 'immer'
import { ROUTES, STATUS_PAYMENT } from '../../../../constants'
import { IOptionsModel } from 'models/shared.models'
import Client from 'models/client'
import {
  CreditNote,
  CreditNoteFiles,
  IInvoiceDraft,
  IInvoice,
  InvoiceFiles,
  InvoiceForm,
  PaymentComplement,
  PaymentComplementFiles,
  TInvoiceTableTabs,
  TCFDIVersion,
} from 'models/invoices.models'
import { STORE_EVENT_TYPES } from 'lib/enums/shared.enums'
import { INVOICES_TABLE_TABS } from 'lib/enums/invoices.enums'
import { toggleChatComponent } from 'lib/helpers/shared.helpers'
import { selectActiveWorkspace, selectInvoicesFilter } from 'lib/helpers/selectors'
import { errorToastOptions, successToastOptions } from 'lib/utils/toast.utils'
import { clearInvoicesLocalFilterValues } from 'lib/utils/Invoices/invoices.utils'
import useStoreEvent from 'hooks/Shared/useStoreEvent'
import useDeleteInvoiceDraft from 'hooks/Invoices/CFDIs/useDeleteInvoiceDraft'
import { IUseDownloadInvoiceFilesVariables } from 'hooks/Invoices/CFDIs/useDownloadInvoiceFiles'
import useDownloadInvoicesCsv from 'hooks/Invoices/CFDIs/useDownloadInvoicesCsv'
import { IUseDownloadInvoicesFilesVariables } from 'hooks/Invoices/CFDIs/useDownloadInvoicesFiles'
import useGetSyncInformation from 'hooks/Invoices/CFDIs/useGetSyncInformation'
import { useAppSelector } from 'store/hooks'
import { ICFDISyncInformation } from 'store/SharedData/sharedData.models'
import { setStoreEvent } from 'store/StoreEvent/storeEvent.reducer'
import { storeFormValue } from 'actions/forms'
import { resetCFDIsFilters } from 'actions/invoices'
import { IApiError } from 'services/Shared/shared.models'
import { fetchAllCfdisMatchingFilters } from 'services/Invoices/invoices.services'
import { toast } from 'react-toastify'
import { ICFDIsContainerProps } from './CFDIsContainer'
import useCFDIsContainerMutations from './useCFDIsContainerMutations'
import useCFDIsContainerQueries from './useCFDIsContainerQueries'
import './styles.scss'

export interface ICFDIsContainerState {
  activeTableTab: TInvoiceTableTabs
  cancelCFDIDialogOpen: boolean
  cancelPayComplementDialogOpen: boolean
  cancelReason: IOptionsModel | null
  openInfo: boolean
  paymentStatusAnchor: HTMLElement | null
  relatedDocsDialogOpen: boolean
  seeMoreAnchor: HTMLElement | null
  selectedCreditNote: any
  selectedPayComplement: any
  tableHeadersAnchor: HTMLElement | null
  validDialog: boolean
  selectedInvoices: IInvoice[]
  /** Loader de tabla mientras se cargan todas las facturas del filtro para selección masiva. */
  isExpandingSelectionToAllFiltered: boolean
  activeInvoice?: IInvoice | IInvoiceDraft
  selectedCfdiToCancel?: {
    uuid: string
    type: 'invoice' | 'creditNote' | 'payComplement'
  } | null

}
export interface IUseCFDIsContainer extends ICFDIsContainerState {
  isCFDIsTab: boolean
  isInvoiceDraftsTab: boolean
  selectedIsCanceled: boolean
  cancelCfdiMutation: UseMutationResult<
    void,
    any,
    {
      invoice: IInvoice
      reason: number
      substitute?: string
    },
    unknown
  >
  cancelPaymentComplementMutation: UseMutationResult<
      void,
      any,
      { payComplement: PaymentComplement; reason: number; substitute?: string },
      unknown
  >

  changeCfdiPaymentStatusMutation: UseMutationResult<
    void,
    any,
    {
      invoice: IInvoice
      status: string
    },
    unknown
  >
  sendCfdiByEmailMutation: UseMutationResult<void, any, IInvoice, unknown>
  downloadCreditNoteQuery: UseQueryResult<CreditNoteFiles, unknown>
  downloadPayComplementQuery: UseQueryResult<PaymentComplementFiles, unknown>
  downloadInvoiceFilesMutation: UseMutationResult<
    InvoiceFiles,
    any,
    IUseDownloadInvoiceFilesVariables,
    unknown
  >
  downloadInvoicesFilesMutation: UseMutationResult<
    string,
    any,
    IUseDownloadInvoicesFilesVariables,
    unknown
  >
  downloadInvoiceCancelNoticeQuery: UseQueryResult<string, unknown>
  payComplementListQuery: UseQueryResult<PaymentComplement[], any>
  creditNoteListQuery: UseQueryResult<CreditNote[], any>
  sendCreditNoteByEmailMutation: UseMutationResult<
    void,
    any,
    {
      creditNote: any
      invoice: IInvoice
    },
    unknown
  >
  sendPayComplementByEmailMutation: UseMutationResult<
    void,
    any,
    {
      payComplement: any
      invoice: IInvoice
    },
    unknown
  >
  CFDIsPaginatedData: {
    ref: MutableRefObject<HTMLDivElement | null>
    isLoading: boolean
    rows: (IInvoice | IInvoiceDraft)[]
    rowCount: number
    paginationMode: 'server'
    itemsPerPage?: number
    pagination?: true
    page?: number
    onPageChange: (newPage: number) => void
  }
  syncInformationQuery: UseQueryResult<ICFDISyncInformation[], IApiError>
  selectedAddonVersion: TCFDIVersion
  updateState: (update: (draft: ICFDIsContainerState) => void) => void
  onResetFilters: () => void
  onDownloadCsv: (list: IInvoice[]) => void
  onChangeCfdiPayStatus: (invoice?: IInvoice, status?: string) => void
  updateInvoiceFormWithInvoiceDataHandler: () => void
  deleteInvoiceDraftHandler: (
    activeWorkspace: number,
    draftId: number
  ) => () => void
  expandSelectionToAllFilteredCFDIs: () => Promise<void>
}

const useCFDIsContainer = ({
  emisor,
  onRedirect,
}: ICFDIsContainerProps): IUseCFDIsContainer => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const activeWorkspace = useAppSelector(selectActiveWorkspace)
  const filters = useAppSelector(selectInvoicesFilter)

  const [cfdisContainerState, setCFDIsContainerState] =
    useState<ICFDIsContainerState>({
      activeTableTab: INVOICES_TABLE_TABS.CFDIS,
      cancelCFDIDialogOpen: false,
      cancelPayComplementDialogOpen: false,
      cancelReason: null,
      openInfo: false,
      paymentStatusAnchor: null,
      relatedDocsDialogOpen: false,
      seeMoreAnchor: null,
      selectedCreditNote: null,
      selectedPayComplement: null,
      tableHeadersAnchor: null,
      validDialog: false,
      selectedInvoices: [],
      isExpandingSelectionToAllFiltered: false,
      selectedCfdiToCancel: null,
    })

  const {
    activeInvoice,
    activeTableTab,
    openInfo,
    relatedDocsDialogOpen,
    seeMoreAnchor,
    selectedCreditNote,
    selectedPayComplement,
  } = cfdisContainerState

  const isCFDIsTab = activeTableTab === INVOICES_TABLE_TABS.CFDIS
  const isInvoiceDraftsTab = activeTableTab === INVOICES_TABLE_TABS.PREFACTURAS

  const updateState = useCallback(
      (update: (draft: ICFDIsContainerState) => void) => {
        setCFDIsContainerState(currentState =>
            produce(currentState, update)
        )
      },
      []
  )

  const {
    CFDIsPaginatedData,
    creditNoteListQuery,
    downloadCreditNoteQuery,
    downloadInvoiceCancelNoticeQuery,
    downloadPayComplementQuery,
    payComplementListQuery,
    refetchInvoices,
  } = useCFDIsContainerQueries({
    activeInvoice,
    isCFDIsTab,
    emisor,
    relatedDocsDialogOpen,
    selectedCreditNote,
    selectedPayComplement,
    onRedirect,
    updateState,
  })

  const {
    cancelCfdiMutation,
    cancelPaymentComplementMutation,
    changeCfdiPaymentStatusMutation,
    downloadInvoiceFilesMutation,
    downloadInvoicesFilesMutation,
    sendCfdiByEmailMutation,
    sendCreditNoteByEmailMutation,
    sendPayComplementByEmailMutation,
  } = useCFDIsContainerMutations({
    refetchInvoices,
    updateState,
  })

  const { onDownloadCsv } = useDownloadInvoicesCsv()

  const syncInformationQuery = useGetSyncInformation(activeWorkspace)

  const deleteInvoiceDraftMutation = useDeleteInvoiceDraft(() => {
    updateState(draft => {
      draft.seeMoreAnchor = null
      draft.activeInvoice = undefined
    })
    refetchInvoices()
  })

  const selectedIsCanceled = [
    STATUS_PAYMENT.PENDING_CANCELED,
    STATUS_PAYMENT.CANCELED,
  ].includes(activeInvoice?.statusPayment?.value ?? '')

  const selectedAddonVersion = parseFloat(String(activeInvoice?.version)) >= 4 ? '4.0' : '3.3'

  const onResetFilters = useCallback(() => {
    clearInvoicesLocalFilterValues()
    dispatch(resetCFDIsFilters())
  }, [dispatch])

  const expandSelectionToAllFilteredCFDIs = useCallback(async () => {
    if (!activeWorkspace || !isCFDIsTab) return

    updateState(draft => {
      draft.isExpandingSelectionToAllFiltered = true
    })

    try {
      const list = await fetchAllCfdisMatchingFilters(activeWorkspace, {
        rfc: emisor?.rfc,
        search: filters?.search,
        advance: filters?.advance,
        type: filters?.type?.value,
        status: filters?.status?.value,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        receptor: filters?.receptor,
      })

      updateState(draft => {
        draft.selectedInvoices = list
        draft.isExpandingSelectionToAllFiltered = false
      })

      toast.success(`Se seleccionaron ${list.length} factura(s)`, {
        ...successToastOptions,
        autoClose: 3500,
      })
    } catch {
      updateState(draft => {
        draft.isExpandingSelectionToAllFiltered = false
      })

      toast.error(
        'No se pudieron cargar todas las facturas. Intenta nuevamente.',
        {
          ...errorToastOptions,
          autoClose: 5000,
        }
      )
    }
  }, [
    activeWorkspace,
    emisor?.rfc,
    filters?.advance,
    filters?.endDate,
    filters?.receptor,
    filters?.search,
    filters?.startDate,
    filters?.status?.value,
    filters?.type?.value,
    isCFDIsTab,
    updateState,
  ])

  const onChangeCfdiPayStatus = (
    invoice?: IInvoice,
    status: string = STATUS_PAYMENT.PENDING
  ) => {
    if (!invoice) return
    updateState(draft => {
      draft.paymentStatusAnchor = null
    })
    changeCfdiPaymentStatusMutation.mutate({ invoice, status })
  }

  const updateInvoiceFormWithInvoiceDataHandler = () => {
    if (!activeInvoice) return

    const typedInvoice = activeInvoice as IInvoiceDraft

    const {
      additionalInformation,
      clients,
      conceptos,
      currency,
      emisor,
      exchangeRate,
      idRequest,
      paymentMethod,
      periodicity,
      periodicityMonth,
      periodicityYear,
      type,
      usoCfdi,
      version,
      wayOfPayment,
    } = typedInvoice

    const formData: Partial<InvoiceForm> = {
      conceptos: conceptos ?? [],
      condiciones: additionalInformation,
      emisor: emisor ?? null,
      formaPago: wayOfPayment,
      idRequest,
      metodoPago: paymentMethod,
      moneda: currency,
      month: periodicityMonth,
      period: periodicity,
      receptor: (clients as unknown as Client) ?? null,
      tipo: type,
      tipoCambio: exchangeRate,
      usoCfdi: usoCfdi,
      version: String(version),
      year: periodicityYear
        ? {
            idCatalogValues: 0,
            label: `${periodicityYear}`,
            value: `${periodicityYear}`,
            enabled: true,
          }
        : undefined,
    }

    for (const [key, value] of Object.entries(formData)) {
      value && dispatch(storeFormValue('createInvoice', key, value))
    }

    dispatch(setStoreEvent(STORE_EVENT_TYPES.SWITCH_INVOICE_DRAFTS_TAB))
    navigate(
      `${ROUTES.ADD_INVOICE.replace(':type', 'income')}${version === '4' ? '?version=4.0' : ''}`
    )
  }

  const deleteInvoiceDraftHandler =
    (activeWorkspace: number, draftId: number) => () =>
      deleteInvoiceDraftMutation.mutate({
        activeWorkspace,
        draftId,
      })

  useStoreEvent(STORE_EVENT_TYPES.CHECK_CFDIS_SYNC_STATUS, () =>
    syncInformationQuery.refetch()
  )

  useStoreEvent(STORE_EVENT_TYPES.SWITCH_INVOICE_DRAFTS_TAB, () =>
    updateState(draft => {
      draft.activeTableTab = INVOICES_TABLE_TABS.PREFACTURAS
    })
  )

  useEffect(() => {
    openInfo || !!seeMoreAnchor
      ? toggleChatComponent(false)
      : toggleChatComponent(true)
  }, [openInfo, seeMoreAnchor])

  useEffect(() => {
    onResetFilters()
    return () => {
      onResetFilters()
    }
  }, [activeTableTab, onResetFilters])

  return {
    ...cfdisContainerState,
    isCFDIsTab,
    isInvoiceDraftsTab,
    selectedIsCanceled,
    cancelCfdiMutation,
    cancelPaymentComplementMutation,
    changeCfdiPaymentStatusMutation,
    sendCfdiByEmailMutation,
    downloadInvoiceFilesMutation,
    downloadInvoicesFilesMutation,
    downloadInvoiceCancelNoticeQuery,
    payComplementListQuery,
    creditNoteListQuery,
    downloadCreditNoteQuery,
    downloadPayComplementQuery,
    sendCreditNoteByEmailMutation,
    sendPayComplementByEmailMutation,
    selectedAddonVersion,
    CFDIsPaginatedData,
    syncInformationQuery,
    updateState,
    onResetFilters,
    onDownloadCsv,
    onChangeCfdiPayStatus,
    updateInvoiceFormWithInvoiceDataHandler,
    deleteInvoiceDraftHandler,
    expandSelectionToAllFilteredCFDIs,
  }
}

export default useCFDIsContainer
